import type { SucursalVenta } from '../services/sucursalesVentaService';
import { persistGlobalIdCompania, readGlobalIdCompania } from './companiaPreferida';
import { persistGlobalNroSuc } from './nroSucPreferida';

export function resolveSucursalPreferidaNro(
  preferidaNro: number | null | undefined,
  sucursales: Array<Pick<SucursalVenta, 'nroSuc'>>,
): number | null {
  if (preferidaNro == null) return null;
  const nro = Number(preferidaNro);
  if (!Number.isFinite(nro) || nro <= 0) return null;
  return sucursales.some((s) => Number(s.nroSuc) === nro) ? nro : null;
}

/**
 * Sucursales de la compañía.
 * Si el catálogo ya tiene idCompania en algún ítem, exige match exacto
 * (no tratar null como “pertenece a todas”).
 */
export function filtrarSucursalesPorCompania<T extends Pick<SucursalVenta, 'nroSuc' | 'idCompania'>>(
  sucursales: T[],
  idCompania: string | null | undefined,
): T[] {
  const id = String(idCompania ?? '').trim();
  if (!id) return sucursales;
  const hayIds = sucursales.some((s) => String(s.idCompania ?? '').trim());
  return sucursales.filter((s) => {
    const idSuc = String(s.idCompania ?? '').trim();
    if (!hayIds) return true;
    return idSuc === id;
  });
}

/**
 * Elige nroSuc para una compañía.
 * Al cambiar compañía (keepCurrentIfValid=false): preferida → storage → primera.
 * Con keepCurrentIfValid=true: conserva la actual si sigue siendo válida.
 */
export function resolveNroSucParaCompania(options: {
  idCompania: string | null | undefined;
  sucursales: Array<Pick<SucursalVenta, 'nroSuc' | 'idCompania'>>;
  preferidaNro?: number | null;
  storedNroSuc?: number | null;
  currentNroSuc?: number | null;
  keepCurrentIfValid?: boolean;
}): number | null {
  const filtradas = filtrarSucursalesPorCompania(options.sucursales, options.idCompania);
  if (!filtradas.length) return null;

  const preferida = resolveSucursalPreferidaNro(options.preferidaNro, filtradas);
  if (!options.keepCurrentIfValid && preferida != null) return preferida;

  const current = Number(options.currentNroSuc ?? 0);
  if (
    options.keepCurrentIfValid &&
    current > 0 &&
    filtradas.some((s) => Number(s.nroSuc) === current)
  ) {
    return current;
  }

  if (preferida != null) return preferida;

  const stored = Number(options.storedNroSuc ?? 0);
  if (stored > 0 && filtradas.some((s) => Number(s.nroSuc) === stored)) {
    return stored;
  }

  const id = String(options.idCompania ?? '').trim();
  if (id) {
    const exactas = filtradas.filter((s) => String(s.idCompania ?? '').trim() === id);
    if (exactas.length) return Number(exactas[0].nroSuc) || null;
  }

  return Number(filtradas[0].nroSuc) || null;
}

export type CompaniaYSucursalInicial = {
  idCompania: string;
  nroSuc: number;
};

/**
 * Entrada a un comprobante nuevo de ventas:
 * 1) sucursal preferida (estrella) + su compañía
 * 2) compañía/sucursal de localStorage si son válidas
 * 3) primera compañía del catálogo + sucursal asociada
 */
export function resolveCompaniaYSucursalInicial(options: {
  sucursales: Array<Pick<SucursalVenta, 'nroSuc' | 'idCompania'>>;
  preferidaNro?: number | null;
  preferidaSucursal?: Pick<SucursalVenta, 'nroSuc' | 'idCompania'> | null;
  companias: Array<{ idCompania: string }>;
  storedIdCompania?: string | null;
  storedNroSuc?: number | null;
}): CompaniaYSucursalInicial | null {
  const { sucursales, preferidaNro, preferidaSucursal, companias } = options;
  if (!sucursales.length) return null;

  const storedId =
    String(options.storedIdCompania ?? readGlobalIdCompania() ?? '').trim() || null;
  const storedNro =
    options.storedNroSuc != null
      ? Number(options.storedNroSuc)
      : Number(localStorage.getItem('nroSuc') || '0') || null;

  const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
  if (preferida != null) {
    const suc =
      preferidaSucursal && Number(preferidaSucursal.nroSuc) === preferida
        ? preferidaSucursal
        : sucursales.find((s) => Number(s.nroSuc) === preferida);
    const idFromSuc = String(suc?.idCompania ?? '').trim();
    const idCompania =
      (idFromSuc && companias.some((c) => c.idCompania === idFromSuc)
        ? idFromSuc
        : null) ||
      (storedId && companias.some((c) => c.idCompania === storedId) ? storedId : null) ||
      companias[0]?.idCompania ||
      idFromSuc ||
      '';
    if (preferida > 0) {
      return { idCompania, nroSuc: preferida };
    }
  }

  const idCompania =
    (storedId && companias.some((c) => c.idCompania === storedId) ? storedId : null) ||
    companias[0]?.idCompania ||
    '';

  const nro = resolveNroSucParaCompania({
    idCompania,
    sucursales,
    preferidaNro,
    storedNroSuc: storedNro,
    keepCurrentIfValid: false,
  });
  if (nro == null || nro <= 0) {
    const first = sucursales[0];
    if (!first) return null;
    return {
      idCompania: idCompania || String(first.idCompania ?? '').trim(),
      nroSuc: Number(first.nroSuc),
    };
  }
  return { idCompania, nroSuc: nro };
}

export function persistCompaniaYSucursalInicial(value: CompaniaYSucursalInicial): void {
  if (value.idCompania) persistGlobalIdCompania(value.idCompania);
  if (value.nroSuc > 0) persistGlobalNroSuc(value.nroSuc);
}

export function applySucursalPreferidaIfNeeded(options: {
  enabled: boolean;
  loading: boolean;
  currentNroSuc: number | null | undefined;
  preferidaNro: number | null | undefined;
  sucursales: Array<Pick<SucursalVenta, 'nroSuc'>>;
  onApply: (nroSuc: number) => void;
  force?: boolean;
}): void {
  const { enabled, loading, currentNroSuc, preferidaNro, sucursales, onApply, force = true } =
    options;
  if (!enabled || loading) return;

  const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
  if (preferida == null) return;

  const current = Number(currentNroSuc ?? 0);
  if (!force && current > 0) return;
  if (current === preferida) return;

  onApply(preferida);
  persistGlobalNroSuc(preferida);
}
